import { ProductCategoryKeys } from '../../../../common/enums/common';
import { TyConfig } from '../../../../providers/config-provider';
import { TyRegionBasic } from '../../../../types';
import { safeGet } from '../../../../utils/objectUtils';
import {
  getCommerceToolsCategoryId,
  getCommerceToolsChannelId,
  getCommerceToolsPriceRange,
  getCommerceToolsMaxPriceRange,
  getSimilarProductsPriceRange,
  getNewTagNumberOfDays,
} from '../../../../utils/configUtils';
import { DateFormat, getFormatedDateAsString } from '../../../../utils/dateUtils';
import { IQueryFilterParam, IQueryFilterParamPerKey, IRange } from '../../productInterfaces';
import { getCommonProductFilters } from '../../../../utils/commerceToolsUtils';
import { getKPRegion } from '../../../../utils/regionUtils';

export enum ProductFilterType {
  NEW_ARRIVALS = 'new_arrivals',
  SIMILAR_PRODUCTS = 'similar_products',
  ABOUT_BRAND = 'about_brand',
  PRODUCT_BY_BRAND = 'product_by_brand',
  PRODUCT_BY_ID = 'product_by_id',
  PRODUCT_BY_SKU = 'product_by_sku',
}

export enum PropertyNotAvailable {
  NOT_AVAILABLE = 'not_available',
  NOTAVAILABLE = 'not available',
}

export interface IQueryFilter {
  config: TyConfig;
  productCategory: ProductCategoryKeys;
  region: TyRegionBasic;
  personalizeData?: any;
  productId: string;
  sku: string;
  productFilterType: ProductFilterType;
  pd?: number | null;
}

export const isNotAvailable = (filter: string) => {
  let notAvailable = false;
  if (
    filter?.toLowerCase() === PropertyNotAvailable.NOTAVAILABLE ||
    filter?.toLowerCase() === PropertyNotAvailable.NOT_AVAILABLE
  ) {
    notAvailable = true;
  }
  return notAvailable;
};

export const getQueryFiltersByType = (queryFilter: IQueryFilter) => {
  const { config, personalizeData, productCategory, productFilterType, productId, sku, pd } =
    queryFilter;
  const { gender, shape, material, price, brand, color, type } = personalizeData;
  const channelId = getCommerceToolsChannelId(config);
  let queryParams: IQueryFilterParamPerKey = createQueryParam(
    {},
    'categories.id',
    getCommerceToolsCategoryId(config, productCategory),
  );
  // queryParams = createQueryParam(queryParams, 'variants.prices.channel.id', channelId);
  if (!channelId) {
    console.error(
      `May not find the user profile for this channel. contact admin product cateegory: ${productCategory}`,
    );
  }
  const priceRangeObj = getPriceRangeObj(config, productCategory, price);
  const similarPriceRangeObj = price ? getSimilarPriceRangeObj(config, price) : null;

  const similarProductsAttributes = [
    { key: 'variants.attributes.FRAME_GENDER', value: gender },
    { key: 'variants.attributes.FRAME_SHAPE_GROUP.label', value: shape },
    { key: 'variants.attributes.COLOR', value: color },
    { key: 'variants.attributes.FRAME_MATERIAL_GROUP.label', value: material },
    { key: 'variants.attributes.FRAME_TYPE_GROUP.label', value: type },
  ];

  switch (productFilterType) {
    case ProductFilterType.NEW_ARRIVALS:
      // {string: "variants.attributes.RELEASE_DATE:range (\"2021-09-13\" to \"2025-09-13\")"}]) {
      const noOfDays = getNewTagNumberOfDays(config, productCategory);
      let fromDate: Date | string = new Date();
      fromDate.setDate(fromDate.getDate() - noOfDays);
      fromDate = getFormatedDateAsString(fromDate, '-', DateFormat.YYYYMMDD);
      const toDate = getFormatedDateAsString(new Date(), '-', DateFormat.YYYYMMDD);
      const newArrivalsPriceRangeObj = getMaxOnlyPriceRangeObj(config, productCategory);
      queryParams = createQueryParam(queryParams, 'variants.attributes.RELEASE_DATE', '', {
        from: fromDate.toString(),
        to: toDate.toString(),
      });
      if (newArrivalsPriceRangeObj) {
        queryParams = createQueryParam(
          queryParams,
          'variants.prices.value.centAmount',
          '',
          newArrivalsPriceRangeObj,
        );
        // queryParams = createQueryParam(queryParams, 'variants.scopedPrice.currentValue.centAmount', '', priceRangeObj)
      }
      break;
    case ProductFilterType.SIMILAR_PRODUCTS:
      // Low to High Price Range Ascending Order
      if (similarPriceRangeObj) {
        queryParams = createQueryParam(
          queryParams,
          'variants.prices.value.centAmount',
          '',
          similarPriceRangeObj,
        );
      }

      // Product attributes to filter by:
      // - FRAME_GENDER
      // - FRAME_SHAPE
      // - COLOR
      // - FRAME_MATERIAL
      // - FRAME_TYPE
      similarProductsAttributes.forEach(({ key, value }) => {
        if (!isNotAvailable(value)) {
          queryParams = createQueryParam(queryParams, key, value);
        }
      });
      if (pd) {
        const minPDRange = {
          from: '*',
          to: String(pd),
        };
        const maxPDRange = {
          from: String(pd),
          to: '*',
        };
        queryParams = !isNotAvailable(String(pd))
          ? createQueryParam(queryParams, 'variants.attributes.MIN_PD', '', minPDRange)
          : queryParams;
        queryParams = !isNotAvailable(String(pd))
          ? createQueryParam(queryParams, 'variants.attributes.MAX_PD', ',', maxPDRange)
          : queryParams;
      }
      break;
    case ProductFilterType.ABOUT_BRAND:
      if (priceRangeObj) {
        queryParams = createQueryParam(
          queryParams,
          'variants.prices.value.centAmount',
          '',
          priceRangeObj,
        );
        // queryParams = createQueryParam(queryParams, 'variants.scopedPrice.currentValue.centAmount', '', priceRangeObj)
      }
      queryParams = createQueryParam(queryParams, 'categories[0].key', productCategory);
      queryParams = createQueryParam(queryParams, 'variants.attributes.BRAND_CODE', brand);
      // queryParams.push(createQueryParam(queryParams,'variants.prices.channel.id', channelId))
      // queryParams.push(createQueryParam(queryParams,'variants.attributes.FRAME_GENDER', formattedGender))
      // queryParams.push(createQueryParam(queryParams,'variants.attributes.FRAME_SHAPE', shape))
      // queryParams.push(createQueryParam(queryParams,'variants.attributes.FRAME_SIZE', size))
      break;
    case ProductFilterType.PRODUCT_BY_BRAND:
      queryParams = createQueryParam(queryParams, 'categories[0].key', productCategory);
      queryParams = createQueryParam(queryParams, 'variants.attributes.BRAND_CODE', brand);
      break;
    case ProductFilterType.PRODUCT_BY_SKU:
      queryParams = createQueryParam(queryParams, 'variants.sku', sku);
      break;
    case ProductFilterType.PRODUCT_BY_ID:
      queryParams = createQueryParam(queryParams, 'id', productId);
      break;
    default:
      queryParams = {};
      break;
  }
  return filterEmptyValueQueryParams(queryParams);
};

const filterEmptyValueQueryParams = (queryParams: IQueryFilterParamPerKey) => {
  const filteredQueryParams = Object.values(queryParams).filter(queryParam => {
    // This validation is to check if ecommerce configuration has empty or not available value returned
    if (queryParam?.value) {
      const queryParamToConsider =
        queryParam?.value?.toLowerCase()?.indexOf('not available') === -1;
      if (queryParamToConsider) {
        return queryParam;
      }
    }
    if (!queryParam?.value && queryParam?.range) {
      return queryParam;
    }
    return undefined;
  });
  return filteredQueryParams;
};

/**
 * from/to price are returned in cent amounts
 * Ex $1 to $80 then
 * Response will be {from: 100, to: 80800}
 * @param config kp config
 * @param productCategory to check it in config
 * @param price of the current product
 * @returns from and to price as an object
 */
const getPriceRangeObj = (config: TyConfig, productCategory: ProductCategoryKeys, price: any) => {
  if (!price) {
    price = '1';
  }
  const priceRange = getCommerceToolsPriceRange(config, productCategory);
  const fromPrice = priceRange || 1;
  const toPrice = price * 1 + (price / 100) * (priceRange || 1);
  const priceRangeObj = {
    from: (fromPrice * 100).toString(),
    to: (toPrice * 10000).toString(),
  };
  return priceRangeObj;
};

const getMaxOnlyPriceRangeObj = (config: TyConfig, productCategory: ProductCategoryKeys) => {
  const priceRange = getCommerceToolsPriceRange(config, productCategory);
  const maxPriceRange = getCommerceToolsMaxPriceRange(config) || 10000;
  const fromPrice = priceRange || 1;
  const priceRangeObj = {
    from: (fromPrice * 100).toString(),
    to: (maxPriceRange * 100).toString(),
  };
  return priceRangeObj;
};

const getSimilarPriceRangeObj = (config: TyConfig, price: any) => {
  const priceConfig = getSimilarProductsPriceRange(config);
  const priceRange = priceConfig || 1;
  const priceValue = parseFloat(price);
  const priceAdjustment = priceValue * priceRange; // config price range applied
  const adjustedFromPrice = priceValue - priceAdjustment < 1 ? 1 : priceValue - priceAdjustment;
  const adjustedToPrice = priceValue + priceAdjustment;
  const priceRangeObj = {
    from: (adjustedFromPrice * 100).toString(),
    to: (adjustedToPrice * 100).toString(),
  };
  return priceRangeObj;
};

export const getQueryVariables = (queryParams: IQueryFilterParam[], config?: any) => {
  const region = getKPRegion()?.toLowerCase();
  let queryFilters = (queryParams || []).map(variable => {
    if (variable.range) {
      const from = safeGet(variable, 'range.from', '');
      const to = safeGet(variable, 'range.to', '');
      if (!from || !to) return undefined;
      return { string: `${variable?.type}:range ("${from}" to "${to}")` };
    }
    if (!variable.value) return undefined;
    return { string: `${variable?.type}:"${variable?.value}"` };
  });
  if (config) getCommonProductFilters({ [region]: config[region] } as TyConfig, queryFilters);
  // Returning only valid filters
  queryFilters = queryFilters?.filter(x => !!x);
  return { queryFilters };
};

const createQueryParam = (
  queryParams: IQueryFilterParamPerKey,
  field: string,
  value: string,
  range?: IRange,
): IQueryFilterParamPerKey => {
  const obj: IQueryFilterParam = {
    type: field,
    value: value,
  };
  if (range) {
    obj['range'] = range;
  }
  if ((value || range) && !(queryParams as any)[field]) {
    queryParams = {
      ...queryParams,
      [field]: obj,
    };
  }
  return queryParams;
};
